/**
 * CoConnect — Public Traceability Page
 * ──────────────────────────────────────────
 * Accessed by scanning a QR code.
 * URL: /t/:code
 * No authentication required — public supply chain transparency.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BrandMark, Card, SmartStatus, Spinner, Alert } from '../components/ui';

const TraceabilityPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    // v1.6.4: use relative URL so it works in production
    fetch(`/api/qr/resolve/${code}`)
      .then(res => {
        if (!res.ok) throw new Error('Invalid QR code');
        return res.json();
      })
      .then(d => setData(d))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="cocoa" />
          <div className="flex items-center gap-2 text-sm text-ink-500">
            <Spinner size="sm" /> Tracing product…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center p-5">
        <Card padding="lg" className="max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-danger-50 dark:bg-danger-500/10 flex items-center justify-center text-danger-600">
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">QR code not found</h2>
          <p className="mt-1.5 text-sm text-ink-500">This code is invalid or has been deactivated.</p>
          <p className="mt-3 text-2xs font-mono text-ink-400">{code}</p>
        </Card>
      </div>
    );
  }

  const { qr, entity } = data;

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 safe-top pb-safe">
      {/* Header */}
      <header className="bg-ink-0 dark:bg-ink-900 border-b border-ink-200 dark:border-ink-800">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <BrandMark size="md" accent="cocoa" />
          <div>
            <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">CoConnect</p>
            <p className="text-xs text-ink-500">Product Traceability</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* QR Code info */}
        <Card padding="md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400">Traceability Code</p>
              <p className="text-sm font-mono font-semibold text-ink-900 dark:text-ink-50">{qr.code}</p>
            </div>
            <div className="text-right">
              <p className="text-2xs uppercase tracking-micro text-ink-400">Type</p>
              <p className="text-sm font-semibold capitalize text-ink-900 dark:text-ink-50">{qr.entity_type}</p>
            </div>
            <div className="text-right">
              <p className="text-2xs uppercase tracking-micro text-ink-400">Scans</p>
              <p className="text-sm font-semibold nums text-ink-900 dark:text-ink-50">{qr.scan_count}</p>
            </div>
          </div>
        </Card>

        {/* Batch traceability — v1.6.4 enterprise 10/10 full chain of custody */}
        {qr.entity_type === 'batch' && entity && (
          <>
            {/* Chain completeness indicator */}
            {entity.chain_completeness && (
              <Card padding="md">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-2xs uppercase tracking-micro text-ink-400">Chain of Custody</p>
                  <p className="text-sm font-bold text-ink-900 dark:text-ink-50">{entity.chain_completeness_score}</p>
                </div>
                <div className="flex gap-1">
                  {[
                    { key: 'farmer_registered', label: 'Farmer', icon: '👤' },
                    { key: 'farm_mapped', label: 'Farm', icon: '🗺️' },
                    { key: 'batch_created', label: 'Batch', icon: '📦' },
                    { key: 'dispatched', label: 'Dispatch', icon: '🚚' },
                    { key: 'intaked', label: 'Warehouse', icon: '🏭' },
                    { key: 'decided', label: 'Decision', icon: '✅' },
                    { key: 'paid', label: 'Payment', icon: '💰' },
                    { key: 'outbound', label: 'Outbound', icon: '📤' },
                  ].map(step => (
                    <div key={step.key} className={`flex-1 text-center p-1.5 rounded ${entity.chain_completeness[step.key] ? 'bg-success-50 dark:bg-success-500/10' : 'bg-ink-100 dark:bg-ink-800'}`}>
                      <div className={`text-sm ${entity.chain_completeness[step.key] ? 'opacity-100' : 'opacity-30'}`}>{step.icon}</div>
                      <div className={`text-2xs ${entity.chain_completeness[step.key] ? 'text-success-700 dark:text-success-500' : 'text-ink-400'}`}>{step.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-2xs text-ink-500 mt-2">
                  Current stage: <strong className="text-ink-900 dark:text-ink-50">{entity.chain_stage}</strong>
                </p>
              </Card>
            )}

            {/* Farmer & Farm info */}
            <Card padding="lg">
              <p className="section-eyebrow mb-2">Origin — Farmer & Farm</p>
              <h2 className="text-xl font-semibold text-ink-900 dark:text-ink-50 mb-4">{entity.farmer_name || 'Unknown Farmer'}</h2>
              <div className="space-y-3">
                <TraceRow label="Farmer" value={entity.farmer_name || '—'} />
                <TraceRow label="Phone" value={entity.farmer_phone || '—'} />
                <TraceRow label="Village" value={entity.village || '—'} />
                <TraceRow label="Cooperative" value={entity.cooperative || '—'} />
                <TraceRow label="KYC Verified" value={entity.kyc_verified ? '✅ Yes' : '❌ No'} />
                {entity.certification_status && entity.certification_status !== 'none' && (
                  <TraceRow label="Certification" value={entity.certification_status} />
                )}
                <div className="divider" />
                <TraceRow label="Farm" value={entity.farm_name || '—'} />
                <TraceRow label="Farm Area" value={entity.farm_area_ha ? `${Number(entity.farm_area_ha).toFixed(1)} ha` : '—'} />
                <TraceRow label="Cocoa Variety" value={entity.cocoa_variety || '—'} />
                <TraceRow label="Planted" value={entity.planting_year || '—'} />
                <TraceRow label="EUDR Status" value={<SmartStatus status={entity.eudr_status} />} />
              </div>
            </Card>

            {/* Quality & Grading */}
            <Card padding="lg">
              <p className="section-eyebrow mb-2">Quality Assessment</p>
              <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50 mb-4">Cocoa Quality Grade</h3>
              <div className="space-y-3">
                <TraceRow label="Final Grade" value={
                  <span className={`text-lg font-bold ${
                    entity.quality_grade === 'A' ? 'text-success-600 dark:text-success-500'
                    : entity.quality_grade === 'B' ? 'text-warning-600 dark:text-warning-500'
                    : 'text-danger-600 dark:text-danger-500'
                  }`}>{entity.quality_grade || '—'}</span>
                } />
                {entity.quality_score && <TraceRow label="Score" value={`${Number(entity.quality_score).toFixed(1)} / 100`} />}
                {entity.grading_method && <TraceRow label="Method" value={entity.grading_method === 'auto' ? 'AI Graded' : 'Rule-based'} />}
                {entity.ai_grade_confidence != null && <TraceRow label="AI Confidence" value={`${(Number(entity.ai_grade_confidence) * 100).toFixed(0)}%`} />}
                {entity.preliminary_quality_grade && entity.preliminary_quality_grade !== entity.quality_grade && (
                  <TraceRow label="Preliminary Grade" value={
                    <span className="text-ink-500 line-through">{entity.preliminary_quality_grade}</span>
                  } />
                )}
                <div className="divider" />
                <TraceRow label="Weight" value={entity.actual_weight ? `${entity.actual_weight} kg` : entity.est_weight ? `${entity.est_weight} kg (est.)` : '—'} />
                {entity.wet_weight_kg && <TraceRow label="Wet Weight" value={`${entity.wet_weight_kg} kg`} />}
                <TraceRow label="Moisture" value={entity.moisture_content ? `${entity.moisture_content}%` : '—'} />
                <TraceRow label="Fermentation" value={entity.fermentation_level || '—'} />
                {entity.bean_count_per_100g && <TraceRow label="Bean Count / 100g" value={entity.bean_count_per_100g} />}
                {(entity.defect_insect_kg > 0 || entity.defect_mold_kg > 0 || entity.defect_flat_kg > 0) && (
                  <TraceRow label="Defects" value={`Insect: ${entity.defect_insect_kg || 0}kg, Mold: ${entity.defect_mold_kg || 0}kg, Flat: ${entity.defect_flat_kg || 0}kg`} />
                )}
                <TraceRow label="Farmer Verified" value={entity.farmer_verified ? '✅ Verified' : '⏳ Pending'} />
                {entity.farmer_verified_at && <TraceRow label="Verified At" value={new Date(entity.farmer_verified_at).toLocaleString()} />}
              </div>
            </Card>

            {/* Logistics & Dispatch */}
            <Card padding="lg">
              <p className="section-eyebrow mb-2">Logistics — Collection & Transport</p>
              <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50 mb-4">Dispatch Tracking</h3>
              <div className="space-y-3">
                <TraceRow label="Collected" value={entity.collected_at ? new Date(entity.collected_at).toLocaleString() : '—'} />
                {entity.harvested_at && <TraceRow label="Harvested" value={new Date(entity.harvested_at).toLocaleDateString()} />}
                {entity.dispatch_id && (
                  <>
                    <div className="divider" />
                    <TraceRow label="Dispatch Status" value={<SmartStatus status={entity.dispatch_status} />} />
                    <TraceRow label="Truck" value={entity.truck_registration || '—'} />
                    {entity.dispatch_departure && <TraceRow label="Departed" value={new Date(entity.dispatch_departure).toLocaleString()} />}
                    {entity.dispatch_delivered && <TraceRow label="Delivered" value={new Date(entity.dispatch_delivered).toLocaleString()} />}
                  </>
                )}
              </div>
            </Card>

            {/* Warehouse & Decision */}
            <Card padding="lg">
              <p className="section-eyebrow mb-2">Warehouse — Receiving & Routing</p>
              <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50 mb-4">Warehouse Processing</h3>
              <div className="space-y-3">
                <TraceRow label="Intaked" value={entity.intaked_at ? new Date(entity.intaked_at).toLocaleString() : '⏳ Pending'} />
                {entity.warehouse_section && <TraceRow label="Warehouse Section" value={entity.warehouse_section} />}
                {entity.batch_decision && (
                  <TraceRow label="Decision" value={
                    <span className={`font-medium ${
                      entity.batch_decision === 'EXPORT' ? 'text-success-600 dark:text-success-500'
                      : entity.batch_decision === 'REJECT' ? 'text-danger-600 dark:text-danger-500'
                      : 'text-info-600 dark:text-info-500'
                    }`}>{entity.batch_decision}</span>
                  } />
                )}
                {entity.decided_at && <TraceRow label="Decided" value={new Date(entity.decided_at).toLocaleString()} />}
                {entity.outbound_at && (
                  <>
                    <div className="divider" />
                    <TraceRow label="Outbound" value={new Date(entity.outbound_at).toLocaleString()} />
                    <TraceRow label="Destination" value={entity.outbound_destination || '—'} />
                    {entity.container_number && <TraceRow label="Container" value={entity.container_number} />}
                  </>
                )}
              </div>
            </Card>

            {/* Payment */}
            <Card padding="lg">
              <p className="section-eyebrow mb-2">Payment — Farmer Compensation</p>
              <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50 mb-4">Financial Record</h3>
              <div className="space-y-3">
                <TraceRow label="Payment Status" value={<SmartStatus status={entity.payment_status} />} />
                <TraceRow label="Price / kg" value={entity.price_per_kg ? `${Number(entity.price_per_kg).toLocaleString()} XAF` : '—'} />
                <TraceRow label="Total Value" value={entity.calculated_amount ? `${Number(entity.calculated_amount).toLocaleString()} XAF` : '—'} />
                {entity.paid_at && <TraceRow label="Paid At" value={new Date(entity.paid_at).toLocaleString()} />}
                {entity.transaction_id && <TraceRow label="Transaction Ref" value={<span className="font-mono text-2xs">{entity.transaction_id}</span>} />}
              </div>
            </Card>

            {/* Bag-level QR codes */}
            {entity.bags && entity.bags.length > 0 && (
              <Card padding="lg">
                <p className="section-eyebrow mb-2">Bag-Level Tracking</p>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50 mb-4">{entity.bags.length} Bags in This Batch</h3>
                <div className="flex flex-wrap gap-2">
                  {entity.bags.map((bag: any, i: number) => (
                    <div key={i} className={`px-3 py-2 rounded-lg text-center ${bag.status === 'RECEIVED' ? 'bg-success-50 dark:bg-success-500/10' : bag.status === 'MISSING' ? 'bg-danger-50 dark:bg-danger-500/10' : 'bg-ink-100 dark:bg-ink-800'}`}>
                      <div className="text-2xs font-mono font-semibold">{bag.bag_qr}</div>
                      <div className="text-2xs text-ink-500">{bag.weight_kg ? `${bag.weight_kg} kg` : '—'}</div>
                      <div className={`text-2xs ${bag.status === 'RECEIVED' ? 'text-success-600 dark:text-success-500' : bag.status === 'MISSING' ? 'text-danger-600 dark:text-danger-500' : 'text-ink-400'}`}>{bag.status}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Harvest notification origin */}
            {entity.harvest_notification && (
              <Card padding="md">
                <p className="section-eyebrow mb-1">Harvest Notification</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono">{entity.harvest_notification.notification_number}</span>
                  <span className="text-2xs text-ink-500">{entity.harvest_notification.expected_bags} bags · {entity.harvest_notification.expected_weight_kg} kg expected</span>
                </div>
              </Card>
            )}

            {/* Blockchain + EUDR */}
            <Card padding="md">
              <p className="section-eyebrow mb-2">Compliance & Integrity</p>
              <div className="space-y-2">
                {entity.eudr_compliant !== undefined && <TraceRow label="EUDR Compliant" value={entity.eudr_compliant ? '✅ Yes' : '❌ No'} />}
                {entity.dds_reference && <TraceRow label="DDS Reference" value={entity.dds_reference} />}
                {entity.blockchain_hash && <TraceRow label="Integrity Hash" value={<span className="font-mono text-2xs">{entity.blockchain_hash.slice(0, 20)}…</span>} />}
              </div>
            </Card>
          </>
        )}

        {/* Farmer profile */}
        {qr.entity_type === 'farmer' && entity && (
          <Card padding="lg">
            <p className="section-eyebrow mb-2">Farmer Profile</p>
            <h2 className="text-xl font-semibold text-ink-900 dark:text-ink-50 mb-4">{entity.name}</h2>
            <div className="space-y-3">
              <TraceRow label="Village" value={entity.village || '—'} />
              <TraceRow label="Cooperative" value={entity.cooperative || '—'} />
              <TraceRow label="KYC Verified" value={entity.kyc_verified ? 'Yes' : 'No'} />
              <TraceRow label="Registered" value={new Date(entity.created_at).toLocaleDateString()} />
            </div>
          </Card>
        )}

        {/* Dispatch */}
        {qr.entity_type === 'dispatch' && entity && (
          <Card padding="lg">
            <p className="section-eyebrow mb-2">Dispatch Record</p>
            <h2 className="text-xl font-semibold text-ink-900 dark:text-ink-50 mb-4">Delivery Tracking</h2>
            <div className="space-y-3">
              <TraceRow label="Status" value={<SmartStatus status={entity.status} />} />
              <TraceRow label="Farmer" value={entity.farmer_name || '—'} />
              <TraceRow label="Village" value={entity.village || '—'} />
              <TraceRow label="Truck" value={entity.truck_reg || '—'} />
              <TraceRow label="Driver" value={entity.driver_name || '—'} />
              {entity.quality_grade && <TraceRow label="Quality" value={<SmartStatus status={entity.quality_grade} />} />}
              {entity.actual_weight && <TraceRow label="Weight" value={`${entity.actual_weight} kg`} />}
              {entity.assigned_at && <TraceRow label="Assigned" value={new Date(entity.assigned_at).toLocaleString()} />}
              {entity.actual_departure && <TraceRow label="Departed" value={new Date(entity.actual_departure).toLocaleString()} />}
              {entity.delivered_at && <TraceRow label="Delivered" value={new Date(entity.delivered_at).toLocaleString()} />}
            </div>
          </Card>
        )}

        {/* Delivery confirmation */}
        {qr.entity_type === 'delivery' && entity && (
          <Card padding="lg">
            <p className="section-eyebrow mb-2">Delivery Confirmation</p>
            <h2 className="text-xl font-semibold text-ink-900 dark:text-ink-50 mb-4">Delivery Receipt</h2>
            <div className="space-y-3">
              <TraceRow label="Confirmed Weight" value={entity.confirmed_weight ? `${entity.confirmed_weight} kg` : '—'} />
              <TraceRow label="Quality Grade" value={<SmartStatus status={entity.quality_grade} />} />
              {entity.moisture_content && <TraceRow label="Moisture" value={`${entity.moisture_content}%`} />}
              <TraceRow label="Farmer" value={entity.farmer_name || '—'} />
              <TraceRow label="Truck" value={entity.truck_reg || '—'} />
              <TraceRow label="Confirmed" value={entity.created_at ? new Date(entity.created_at).toLocaleString() : '—'} />
            </div>
          </Card>
        )}

        <p className="text-center text-2xs text-ink-400 mt-6">
          Powered by CoConnect · HEFARCAM Cooperative · Cameroon
        </p>
      </main>
    </div>
  );
};

const TraceRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-sm text-ink-500 dark:text-ink-400">{label}</span>
    <span className="text-sm font-medium text-ink-900 dark:text-ink-50 text-right">{value}</span>
  </div>
);

export default TraceabilityPage;
